import type { RefObject } from 'react';
import type { ScrollView, View } from 'react-native';

type MeasureRelativeTo = Parameters<View['measureLayout']>[0];

/**
 * ScrollView exposes its inner content view at runtime (native and web) but
 * the RN type definitions only declare the deprecated numeric accessor.
 */
type ScrollViewWithInnerRef = ScrollView & {
  getInnerViewRef?: () => MeasureRelativeTo | null | undefined;
};

/** Breathing room between the scroll view's top edge and the target. */
const TOP_MARGIN = 16;

/**
 * Scrolls `scrollRef` so `targetRef` sits just below the top edge.
 *
 * Measures against the scroll view's inner content view instance rather than
 * its node handle: under Fabric, `measureLayout` silently ignores numeric
 * handles, so the callback would never fire.
 */
export const scrollIntoView = (
  scrollRef: RefObject<ScrollView | null>,
  targetRef: RefObject<View | null>,
): void => {
  const scrollView = scrollRef.current as ScrollViewWithInnerRef | null;
  const target = targetRef.current;
  if (!scrollView || !target) return;

  const contentView = scrollView.getInnerViewRef?.();
  if (!contentView) return;

  target.measureLayout(contentView, (_x, y) => {
    scrollView.scrollTo({ y: Math.max(0, y - TOP_MARGIN), animated: true });
  });
};
